/**
 * Microsoft 365 Connection Button
 * Shows connection status and handles OAuth flow
 */

import { useState, useEffect } from 'react'

// Microsoft icon SVG
const MicrosoftIcon = () => (
  <svg viewBox="0 0 23 23" className="w-4 h-4" fill="currentColor">
    <path fill="#f25022" d="M1 1h10v10H1z"/>
    <path fill="#00a4ef" d="M1 12h10v10H1z"/>
    <path fill="#7fba00" d="M12 1h10v10H12z"/>
    <path fill="#ffb900" d="M12 12h10v10H12z"/>
  </svg>
)

export default function M365Button() {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  // A refused connection has to be visible. The callback can now reject a
  // mailbox that isn't this user's, and a rejection that only reaches the
  // console looks identical to nothing happening — so the user clicks Connect
  // again, and again.
  const [connectError, setConnectError] = useState(null)
  // Set only when the tenant hasn't approved Trendy. The user can't fix that
  // themselves, so the remedy is a link they forward to IT rather than a retry.
  const [consentUrl, setConsentUrl] = useState(null)

  // Check M365 status on mount
  useEffect(() => {
    checkStatus()

    // Check for OAuth callback params
    const params = new URLSearchParams(window.location.search)
    if (params.get('m365_connected') === 'true') {
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname)
      checkStatus()
    } else if (params.get('m365_error')) {
      const error = params.get('m365_error')
      const desc = params.get('m365_error_desc')
      console.error('M365 OAuth error:', error, desc)
      setConnectError(
        desc ||
        'Microsoft 365 could not be connected. Please try again, or contact an administrator.'
      )
      setConsentUrl(params.get('m365_consent_url'))
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const checkStatus = async () => {
    try {
      const token = localStorage.getItem('amtrend_token')
      console.log('M365 checkStatus - token exists:', !!token)
      if (!token) {
        setLoading(false)
        return
      }

      const response = await fetch('/api/auth/m365/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      console.log('M365 status response:', response.status)

      if (response.ok) {
        const data = await response.json()
        console.log('M365 status data:', data)
        setStatus(data)
      } else {
        console.error('M365 status failed:', response.status)
      }
    } catch (err) {
      console.error('Failed to check M365 status:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleConnect = async () => {
    console.log('M365 Connect clicked')
    setConnectError(null)
    setConnecting(true)
    try {
      const token = localStorage.getItem('amtrend_token')
      console.log('Token exists:', !!token)

      const response = await fetch('/api/auth/m365/login', {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      console.log('M365 login response status:', response.status)

      if (response.ok) {
        const data = await response.json()
        console.log('Auth URL received:', data.auth_url?.substring(0, 50) + '...')
        // Redirect to Microsoft OAuth
        window.location.href = data.auth_url
      } else {
        const errorData = await response.json().catch(() => ({}))
        console.error('Failed to get auth URL:', response.status, errorData)
        setConnecting(false)
      }
    } catch (err) {
      console.error('Failed to start M365 login:', err)
      setConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      const token = localStorage.getItem('amtrend_token')
      const response = await fetch('/api/auth/m365/disconnect', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        setStatus({ connected: false })
      }
    } catch (err) {
      console.error('Failed to disconnect M365:', err)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400 px-3 py-2">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
        Checking M365...
      </div>
    )
  }

  // Not configured - show anyway for debugging
  if (status && status.configured === false) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-400">
        <MicrosoftIcon />
        <span>M365 not configured</span>
      </div>
    )
  }

  // Connected. `can_draft === false` means this token predates Mail.ReadWrite —
  // mail still reads fine, but Trendy cannot prepare drafts until they
  // reconnect. Shown as a distinct state rather than folded into either
  // "connected" (which would hide it until a draft failed) or "disconnected"
  // (which would understate what still works).
  if (status?.connected) {
    const limited = status.can_draft === false
    return (
      <div
        className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border ${
          limited
            ? 'bg-amber-50 border-amber-200'
            : 'bg-green-50 border-green-200'
        }`}
      >
        <div className="flex items-center gap-2 text-xs">
          <div className={`w-2 h-2 rounded-full ${limited ? 'bg-amber-500' : 'bg-green-500'}`}></div>
          <MicrosoftIcon />
          <span className="text-gray-700">{limited ? 'Reconnect needed' : 'Connected'}</span>
        </div>
        {limited && (
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="text-xs font-medium text-amber-800 underline hover:text-amber-900 disabled:opacity-50"
            title="Reconnect to let Trendy prepare drafts in your Outlook"
          >
            Reconnect
          </button>
        )}
        {/* Which mailbox this account acts as. `mailbox` is the bound UPN from
            user_m365_identity; user_email was never actually returned by the
            status endpoint, so this label had always rendered empty. */}
        {(status.mailbox || status.user_email) && (
          <span
            className="text-xs text-gray-500 truncate max-w-[100px]"
            title={status.mailbox || status.user_email}
          >
            {(status.mailbox || status.user_email).split('@')[0]}
          </span>
        )}
        <button
          onClick={handleDisconnect}
          className="text-xs text-gray-400 hover:text-red-500 transition-colors ml-1"
          title="Disconnect Microsoft 365"
        >
          ×
        </button>
      </div>
    )
  }

  // Not connected
  return (
    <div className="w-full">
      {connectError && (
        <div
          role="alert"
          className="mb-2 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg"
        >
          <p>{connectError}</p>
          {consentUrl && (
            <a
              href={consentUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block font-medium underline break-all"
            >
              Administrator approval link
            </a>
          )}
        </div>
      )}
      <button
        onClick={handleConnect}
        disabled={connecting}
        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 border border-gray-200"
        title="Connect Microsoft 365 for email, calendar, and SharePoint access"
      >
        {connecting ? (
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        ) : (
          <MicrosoftIcon />
        )}
        <span>{connecting ? 'Connecting...' : 'Connect Microsoft 365'}</span>
      </button>
    </div>
  )
}
